import { Tooltip } from '@clickguard/design-system'
import { ModalTypes } from 'components/basic-blocks/g-modal/types'
import { ConnectAdAccountComponent } from 'components/setup-v2'
import { useStores } from 'hooks'
import { useAccountStore, useAssetStore, useUiStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { LIME_CTA_CLASS } from './constants'

/**
 * §2 — Empty state shown when the selected website has no Google Ads account
 * connected. Figma 6418-25648 (hover 6418-27218). Spec B2.
 */
export const EmptyNoAdAccount = () => {
	const account = useAccountStore((s) => s.account)
	const { asset, refreshAdAccounts } = useAssetStore(
		useShallow((s) => ({
			asset: s.asset,
			refreshAdAccounts: s.refreshAdAccounts
		}))
	)
	const { userStore } = useStores()
	const user = userStore.user
	const setModalContent = useUiStore((s) => s.setModalContent)
	const setModalState = useUiStore((s) => s.setModalState)

	const handleConnect = () => {
		if (!account || !asset || !user) return
		setModalContent({
			type: ModalTypes.INFO,
			actionsEnabled: false,
			showIcon: false,
			rootClassName: 'bg-transparent',
			body: (
				<ConnectAdAccountComponent
					account={account}
					asset={asset}
					user={user}
					isOnboarding={false}
					onSuccess={async () => {
						await refreshAdAccounts()
						setModalState(false)
					}}
				/>
			)
		})
	}

	return (
		<div className="rounded-xl border border-t-checkbox bg-white p-6">
			<div className="flex flex-col items-center gap-[14px] rounded-xl border border-dashed border-t-checkbox bg-table-band-subtle px-6 py-[36px]">
				<div className="flex flex-col items-center justify-center gap-3">
					<img
						src="/refunds-empty-no-account.svg"
						alt=""
						className="h-12 w-12"
					/>
					<div className="flex flex-col items-center gap-[6px]">
						<h3 className="text-[16px] font-medium leading-[22px] text-brand-tint-text-strong">
							No Google Ads accounts connected yet
						</h3>
						<p className="w-[392px] text-center text-[14px] leading-[18px] text-t-default">
							Connect an account to start automating refund requests.
						</p>
					</div>
				</div>
				<div className="h-[4px] w-px" />
				<button
					type="button"
					onClick={handleConnect}
					className={`flex items-center justify-center px-[14px] py-[10px] text-[14px] transition ${LIME_CTA_CLASS}`}
				>
					Connect Google Ads account
				</button>
				<Tooltip
					theme="dark"
					position="bottom"
					showArrow
					content="How it works"
					description={
						<span>
							ClickGuard detects invalid clicks and automatically submits refund
							claims to Google on your behalf. If eligible, Google will issue
							credits to your Google Ads account under{' '}
							<strong>Billing → Transactions → Adjustments</strong>. Please note
							that Google&apos;s review process can take up to 3 months.
						</span>
					}
				>
					<span className="cursor-help text-center text-[14px] font-medium text-brand-tint-text underline">
						How it works
					</span>
				</Tooltip>
			</div>
		</div>
	)
}
